import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Stack } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import ServiceFormHeader from './serviceFormHeader';
import ServiceNewForm from './serviceNewForm';
import ServiceViewForm from './serviceViewForm';
import ServiceEditForm from './serviceEditForm';
import CompleteServiceForm from './completeServiceForm';
import RenewServiceForm from './serviceRenewForm';
import NurseChangeForm from './nurseChangeForm';
import ServiceNurseViewForm from './serviceNurseViewForm';
import { HomeContext } from '../utils/contexts/homeContext';
import { ServiceFormContext } from '../utils/contexts/serviceFormContext';
import { FormMode, ServiceNurseStatus, CurrentActiveServicePage, ConstantValues } from '../utils/extensions/constantEx'
import {
    GetDateFromDayJSValue, GetFullPhoneNo, PatientAPIUrl, GetNoToCurrencyFormat, GetNumberFromCurrencyFormat
} from '../utils/extensions/functionEx';
import PageLoading from '../shared/components/backdrop';
import { GetServiceTypesAPIUrl, NurseAPIUrl, PatientNurseChangeAPIUrl } from '../utils/extensions/functionEx';
import { useCookies } from 'react-cookie';

export default function ServiceForm(props) {

    const { formMode, navigateFromPage } = props;

    const {
        SetServiceTypes, SetSelectedPatientService, SetPageLoading, SetSelectedNurse, isPageLoadingOpen, selectedNurse, patientServiceNewFormValues,
        SetIsShowValidationMsgs, serviceTypes, selectedPatientService, SetNewFormValues, changeNurseFormInitalState, SetIsShowPaymentForm
    } = useContext(ServiceFormContext);
    const { ShowAPIError, OpenErrorAlert, OpenSuccessAlert, homeState, ClearLoggedUser } = useContext(HomeContext);

    const navigate = useNavigate();

    const [formOpenMode, setFormOpenMode] = useState(FormMode.None);
    const [formHeaderText, setFormHeaderText] = useState('New Patient Service');
    const [expanded, setExpanded] = useState('');
    const [selectedServiceType, setSelectedServiceType] = useState(null);
    const [isServiceClosed, setIsServiceClosed] = useState(false);
    const [isOpenRenewServiceForm, setIsOpenRenewServiceForm] = useState(false);
    const [isOpenCloseServiceForm, setIsOpenCloseServiceForm] = useState(false);
    const [openedPage, setOpenedPage] = useState(CurrentActiveServicePage.None);
    const [serviceNursesCount, setServiceNursesCount] = useState(props.serviceNurseCount ?? 0);
    const [patientAddressId, setPatientAddressId] = useState(ConstantValues.DefaultContactAddressId);

    const [cookies, setCookie] = useCookies(['hplus_home_care_user_session_code']);

    useEffect(() => {

        async function fetchServiceTypes() {
            try {
                const response = await fetch(GetServiceTypesAPIUrl())
                if (response.status === 200) {
                    SetServiceTypes(await response.json());
                } else {
                    ShowAPIError(response)
                    SetServiceTypes([]);
                }
            }
            catch (error) {
                OpenErrorAlert(error);
                SetServiceTypes([]);
            }
        }

        SetPageLoading(true);
        fetchServiceTypes();
        SetSelectedPatientService(null);

        if (formMode === FormMode.NewMode && props.selectedNurseId > 0) {
            getNurse(props.selectedNurseId);
            setFormOpenMode(FormMode.NewMode);
            setOpenedPage(CurrentActiveServicePage.NewPage);
        } else if (formMode === FormMode.ViewMode && props.serviceNurseId > 0) {
            getPatientService(props.serviceId, props.serviceNurseId);
        }
    }, [formMode, props.serviceId, props.serviceNurseId, props.selectedNurseId, navigateFromPage]);

    useEffect(() => {
        const tempFormValue = {
            patientName: '',
            patientMobileNo: '',
            patientPlace: '',
            patientAddress: '',
            patientAddressId: ConstantValues.DefaultContactAddressId,
            patientBusRoute: '',
            patientPhone1: '',
            patientPhone2: '',
            deliveryNo: 0,
            selectedServiceType: null,
            serviceStartDate: null,
            serviceDutyDays: 0,
            serviceTotalFee: 0,
            nurseSalary: 0,
            serviceRemarks: '',
            patientFamilies: [],
            patientDocuments: [],
            removedPatientDocIds: [],
            removedDocIds: []
        };
        SetNewFormValues(tempFormValue);
        setPatientAddressId(ConstantValues.DefaultContactAddressId);
    }, [formOpenMode]);

    useEffect(() => {
        if (formOpenMode === FormMode.NewMode) {
            setFormHeaderText('New Patient Service');
            setIsServiceClosed(false);
            return;
        }

        let tempIsServiceClosed = false;
        let tempHeaderText = '';
        let serviceStatusText = '';

        if (selectedPatientService && selectedPatientService.nurse_status_id === ServiceNurseStatus.Completed) {
            tempIsServiceClosed = true;
        }

        if (formOpenMode === FormMode.EditMode || formOpenMode === FormMode.ViewMode) {
            tempHeaderText = formOpenMode === FormMode.EditMode ? 'Edit Patient Service' : 'View Patient Service';
            serviceStatusText = 'On Going';

            if (tempIsServiceClosed) {
                serviceStatusText = 'Closed';
            }
        }
        setIsServiceClosed(tempIsServiceClosed);
        setFormHeaderText(serviceStatusText.length > 0 ? (tempHeaderText + ' (' + serviceStatusText + ')') : tempHeaderText);
    }, [selectedPatientService, formOpenMode]);

    useEffect(() => {
        if (openedPage === CurrentActiveServicePage.ChangeNursePage) {
            setFormOpenMode(FormMode.None);
            setFormHeaderText('Change Service Nurse');
            setIsServiceClosed(false);
            return;
        }
    }, [openedPage]);

    useEffect(() => {
        if (selectedPatientService && serviceTypes) {
            const tempTypes = serviceTypes.filter(col => col.id === selectedPatientService.service_type_id);
            if (tempTypes && tempTypes.length > 0) {
                setSelectedServiceType(tempTypes[0]);
            }
        }
    }, [selectedPatientService, serviceTypes]);

    const getNurse = async (nurseId) => {
        try {
            const headers = new Headers();
            headers.append("Hc-App-Auth-Token", cookies.hplus_home_care_user_session_code);
            const response = await fetch(NurseAPIUrl(homeState.loggedUser.companyCode, nurseId), { headers });
            if (response.status === 401) {
                OpenErrorAlert('User login history expired, please log in again.');
                setCookie('hplus_home_care_user_session_code', '', { path: '/' });
                ClearLoggedUser();
            } else if (response.status === 200) {
                const tempNurseJson = await response.json();
                const tempNurse = {
                    id: tempNurseJson.id,
                    code: tempNurseJson.code,
                    nurseName: tempNurseJson.nurse_name,
                    phoneNo: GetFullPhoneNo(tempNurseJson.phone1, tempNurseJson.phone2, tempNurseJson.phone3),
                    advanceAmount: GetNoToCurrencyFormat(tempNurseJson.advance_amount)
                };
                SetSelectedNurse(tempNurse);
                SetPageLoading(false);
            } else {
                SetSelectedNurse(null);
                ShowAPIError(response);
                SetPageLoading(false);
            }
        }
        catch (error) {
            SetSelectedNurse(null);
            OpenErrorAlert(error);
            SetPageLoading(false);
        }
    }

    const getPatientService = async (serviceId, serviceNurseId) => {
        SetPageLoading(true);
        try {
            const headers = new Headers();
            headers.append("Hc-App-Auth-Token", cookies.hplus_home_care_user_session_code);
            const response = await fetch(PatientAPIUrl(homeState.loggedUser.companyCode, serviceId, serviceNurseId), { headers });
            if (response.status === 401) {
                OpenErrorAlert('User login history expired, please log in again.');
                setCookie('hplus_home_care_user_session_code', '', { path: '/' });
                ClearLoggedUser();
            } else if (response.status === 200) {
                const tempPatientJson = await response.json();
                setServiceNursesCount(tempPatientJson.service_nurse_count);
                getNurse(tempPatientJson.nurse_id);
                SetSelectedPatientService(tempPatientJson);
                setFormOpenMode(FormMode.ViewMode);
                setOpenedPage(CurrentActiveServicePage.ViewPage);
            } else {
                SetSelectedNurse(null);
                SetSelectedPatientService(null);
                ShowAPIError(response);
                setServiceNursesCount(0)
            }
            handlePageLoadingClose();
        }
        catch (error) {
            SetSelectedNurse(null);
            SetSelectedPatientService(null);
            setServiceNursesCount(0);
            OpenErrorAlert(error);
            handlePageLoadingClose();
        }
    }

    const handlePageLoadingClose = () => {
        SetPageLoading(false);
    }

    const handleAccordionChange = (panel) => (event, newExpanded) => {
        setExpanded(newExpanded ? panel : false);
    }

    const handleEditClick = () => {
        setExpanded(false);
        setFormOpenMode(FormMode.EditMode);
        setOpenedPage(CurrentActiveServicePage.EditPage);
    }

    const handleCancelClick = () => {
        if (openedPage === CurrentActiveServicePage.ChangeNursePage) {
            handleCloseNurseChangeClick();
            return;
        }

        if (formOpenMode === FormMode.ViewMode) {
            navigate("/search-services", {
                replace: false
            });
            return;
        }

        if (formOpenMode === FormMode.EditMode && selectedPatientService
            && selectedPatientService.service_id && selectedPatientService.service_id > 0
            && selectedPatientService.service_nurse_id && selectedPatientService.service_nurse_id > 0) {

            getPatientService(selectedPatientService.service_id, selectedPatientService.service_nurse_id);
            return;
        }
        navigate("/search-nurses", { replace: false });
    }

    const handleFormSaveClick = async () => {
        try {
            let tempFormJson = null;
            if (openedPage === CurrentActiveServicePage.ChangeNursePage) {
                tempFormJson = await handleNurseChangeSave();
                if (tempFormJson) {
                    await getPatientService(tempFormJson.service_id, tempFormJson.service_nurse_id);
                }
                return;
            }
            if (!isFormValid()) {
                SetIsShowValidationMsgs(true);
                return;
            }
            SetIsShowValidationMsgs(false);

            if (formOpenMode === FormMode.NewMode) {
                tempFormJson = await addNewServiceAPI();
            }
            else if (formOpenMode === FormMode.EditMode) {
                tempFormJson = await updateServiceAPI();
            }
            if (tempFormJson) {
                await getPatientService(tempFormJson.service_id, tempFormJson.service_nurse_id);
                return;
            }
            SetPageLoading(false);
        }
        catch (error) {
            OpenErrorAlert(error);
            SetPageLoading(false);
        }
    }

    const addNewServiceAPI = async () => {
        try {
            const tempPatient = getServiceFormValues();

            const headers = new Headers();
            headers.append("Accept", "application/json");
            headers.append("Accept-Encoding", "application/json");
            headers.append("Content-Type", "application/json");
            headers.append("Hc-App-Auth-Token", cookies.hplus_home_care_user_session_code);
            const response = await fetch(PatientAPIUrl(homeState.loggedUser.companyCode), {
                method: "POST",
                headers: headers,
                body: JSON.stringify(tempPatient)
            });
            if (response.status === 401) {
                OpenErrorAlert('User login history expired, please log in again.');
                setCookie('hplus_home_care_user_session_code', '', { path: '/' });
                ClearLoggedUser();
            } else if (response.status === 201) {
                OpenSuccessAlert('New patient service added successfully.');
                return await response.json();
            }
            ShowAPIError(response);
            return null;
        }
        catch (error) {
            OpenErrorAlert(error)
            return null;
        }
    }

    const updateServiceAPI = async () => {
        try {
            const tempPatient = getServiceFormValues();

            const headers = new Headers();
            headers.append("Accept", "application/json");
            headers.append("Accept-Encoding", "application/json");
            headers.append("Content-Type", "application/json");
            headers.append("Hc-App-Auth-Token", cookies.hplus_home_care_user_session_code);
            const response = await fetch(PatientAPIUrl(homeState.loggedUser.companyCode), {
                method: "PUT",
                headers: headers,
                body: JSON.stringify(tempPatient)
            });
            if (response.status === 401) {
                OpenErrorAlert('User login history expired, please log in again.');
                setCookie('hplus_home_care_user_session_code', '', { path: '/' });
                ClearLoggedUser();
            } else if (response.status === 200) {
                OpenSuccessAlert('Patient service updated successfully.');
                return await response.json();
            }
            ShowAPIError(response);
            return null;
        }
        catch (error) {
            OpenErrorAlert(error)
            return null;
        }
    }

    const isFormValid = () => {
        const tempFormValue = { ...patientServiceNewFormValues };
        if (tempFormValue.patientName.trim().length === 0 || tempFormValue.patientMobileNo.trim().length === 0 || tempFormValue.patientPlace.trim().length === 0
            || !tempFormValue.selectedServiceType || tempFormValue.serviceDutyDays <= 0 || parseFloat(tempFormValue.serviceTotalFee) < 0
            || !selectedNurse || parseFloat(tempFormValue.nurseSalary) < 0) {
            return false;
        }
        return true;
    }

    const getServiceFormValues = () => {
        const tempFormValue = { ...patientServiceNewFormValues };

        let addressId = tempFormValue.patientAddressId;
        if (tempFormValue.patientAddressId === ConstantValues.DefaultContactAddressId && tempFormValue.patientAddress.trim().length > 0) {
            addressId = 0;
        }

        const tempServiceReqModel = {
            service_id: formOpenMode === FormMode.EditMode && selectedPatientService ? selectedPatientService.service_id : 0,
            service_nurse_id: formOpenMode === FormMode.EditMode && selectedPatientService ? selectedPatientService.service_nurse_id : 0,
            patient_id: formOpenMode === FormMode.EditMode && selectedPatientService ? selectedPatientService.patient_id : 0,
            patient_name: tempFormValue.patientName.trim(),
            address: tempFormValue.patientAddress.trim(),
            address_id: addressId,
            place: tempFormValue.patientPlace.trim(),
            bus_route: tempFormValue.patientBusRoute.trim(),
            phone1: tempFormValue.patientMobileNo.trim(),
            phone2: tempFormValue.patientPhone1.trim(),
            phone3: tempFormValue.patientPhone2.trim(),
            delivery_no: tempFormValue.deliveryNo,
            nurse_id: selectedNurse.id,
            service_type_id: tempFormValue.selectedServiceType.id,
            duty_days: tempFormValue.serviceDutyDays,
            remarks: tempFormValue.serviceRemarks.trim(),
            start_date: GetDateFromDayJSValue(tempFormValue.serviceStartDate),
            total_salary: GetNumberFromCurrencyFormat(tempFormValue.serviceTotalFee),
            nurse_salary: GetNumberFromCurrencyFormat(tempFormValue.nurseSalary),
            relatives: getFamilyModels(),
            documents: getDbDocuments(),
            removed_patient_doc_ids: [...tempFormValue.removedPatientDocIds],
            removed_doc_ids: [...tempFormValue.removedDocIds],
            is_nurse_changed: serviceNursesCount > 0 ? true : false
        };
        return tempServiceReqModel;
    }

    const getFamilyModels = () => {
        const tempDbFamilies = [];
        const tempFormValue = { ...patientServiceNewFormValues };

        tempFormValue.patientFamilies.forEach((tempPatientFamily) => {
            const tempDbFamily = {
                id: tempPatientFamily.id,
                family_name: tempPatientFamily.name,
                family_relation: tempPatientFamily.relation,
                family_phone_no: tempPatientFamily.phoneNo
            };
            tempDbFamilies.push(tempDbFamily);
        });
        return tempDbFamilies;
    }

    const getDbDocuments = () => {
        const dbDocs = [];
        const tempFormValue = { ...patientServiceNewFormValues };

        tempFormValue.patientDocuments.filter(col => col.id < 0).forEach((tempPatientDocument) => {
            const tempDbDoc = {
                id: tempPatientDocument.id,
                document_id: tempPatientDocument.uploadedFileId,
                document_type: tempPatientDocument.documentType,
                document_no: tempPatientDocument.docNo,
                notes: tempPatientDocument.notes
            };
            dbDocs.push(tempDbDoc);
        });
        return dbDocs;
    }

    const handleCloseServiceClick = () => {
        setIsOpenCloseServiceForm(true);
    }

    const handleHideCloseServiceClick = async (isServiceCompleted, serviceId, serviceNurseId) => {
        setIsOpenCloseServiceForm(false);
        if (isServiceCompleted) {
            await getPatientService(serviceId, serviceNurseId);
        }
    }

    const handleNurseChangeClick = () => {
        setFormOpenMode(FormMode.None);
        setOpenedPage(CurrentActiveServicePage.ChangeNursePage);
    }

    const handleCloseNurseChangeClick = () => {
        setFormOpenMode(FormMode.ViewMode);
        setOpenedPage(CurrentActiveServicePage.ViewPage);
    }

    const handleNurseChangeSave = async () => {
        try {
            const tempFormValue = { ...changeNurseFormInitalState };
            if (!tempFormValue.currentNurseCompletedDate || tempFormValue.currentNurseCompletedDays <= 0 || tempFormValue.newNurseDutyDays <= 0
                || !tempFormValue.newNurseStartDate || tempFormValue.serviceId <= 0 || tempFormValue.currentServiceNurseId <= 0
                || tempFormValue.newNurseId <= 0) {

                OpenErrorAlert('Please enter valid details.');
                return null;
            }
            SetPageLoading(true);
            const tempAPIFormValues = {
                service_id: tempFormValue.serviceId,
                current_service_nurse_id: tempFormValue.currentServiceNurseId,
                current_nurse_completed_date: GetDateFromDayJSValue(tempFormValue.currentNurseCompletedDate),
                current_nurse_completed_days: tempFormValue.currentNurseCompletedDays,
                current_nurse_completed_days_salary: tempFormValue.currentNurseCompletedDaysSalary,
                new_nurse_id: tempFormValue.newNurseId,
                new_nurse_duty_days: tempFormValue.newNurseDutyDays,
                new_nurse_start_date: GetDateFromDayJSValue(tempFormValue.newNurseStartDate),
                new_nurse_salary: tempFormValue.newNurseSalary
            };

            const headers = new Headers();
            headers.append("Accept", "application/json");
            headers.append("Accept-Encoding", "application/json");
            headers.append("Content-Type", "application/json");
            headers.append("Hc-App-Auth-Token", cookies.hplus_home_care_user_session_code);
            const response = await fetch(PatientNurseChangeAPIUrl(homeState.loggedUser.companyCode), {
                method: "PUT",
                headers: headers,
                body: JSON.stringify(tempAPIFormValues)
            });
            if (response.status === 401) {
                OpenErrorAlert('User login history expired, please log in again.');
                setCookie('hplus_home_care_user_session_code', '', { path: '/' });
                ClearLoggedUser();
            } else if (response.status === 200) {
                OpenSuccessAlert('Patient service nurse changed successfully.');
                return await response.json();
            }
            ShowAPIError(response);
            return null;
        }
        catch (error) {
            OpenErrorAlert(error)
            return null;
        } finally {
            SetPageLoading(false);
        }
    }

    const handleOpenRenewServiceClick = () => {
        setIsOpenRenewServiceForm(true);
    }

    const handleCloseRenewServiceClick = async (isServiceCompleted, serviceId, serviceNurseId) => {
        setIsOpenRenewServiceForm(false);
        if (isServiceCompleted) {
            await getPatientService(serviceId, serviceNurseId);
        }
    }

    const handleNewPaymentClick = () => {
        SetIsShowPaymentForm(true);
    }


    return (

        <Box>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={"en-in"}>

                <Stack spacing={0}>
                    {
                        isPageLoadingOpen &&
                        <PageLoading isOpenPageLoading={isPageLoadingOpen} handleClose={handlePageLoadingClose} />
                    }

                    {
                        openedPage === CurrentActiveServicePage.ViewPage && selectedPatientService && isOpenCloseServiceForm &&
                        <CompleteServiceForm isOpen={isOpenCloseServiceForm} selectedPatientService={selectedPatientService}
                            handleCloseClick={handleHideCloseServiceClick} />
                    }

                    {
                        openedPage === CurrentActiveServicePage.ViewPage && selectedPatientService && isOpenRenewServiceForm &&
                        <RenewServiceForm isOpen={isOpenRenewServiceForm} selectedPatientService={selectedPatientService}
                            handleCloseClick={handleCloseRenewServiceClick} />
                    }

                    <ServiceFormHeader formMode={formOpenMode} openedPage={openedPage} isServiceClosed={isServiceClosed} formHeaderText={formHeaderText}
                        handleEditClick={handleEditClick} handleCancelClick={handleCancelClick} handleNurseChangeClick={handleNurseChangeClick}
                        handleSaveClick={handleFormSaveClick} handleCloseServiceClick={handleCloseServiceClick}
                        handleOpenRenewServiceClick={handleOpenRenewServiceClick} handleNewPaymentClick={handleNewPaymentClick} />

                    {
                        openedPage === CurrentActiveServicePage.NewPage &&
                        <ServiceNewForm expanded={expanded} handleAccordionChange={handleAccordionChange} />
                    }

                    {
                        openedPage === CurrentActiveServicePage.ViewPage && selectedPatientService && serviceNursesCount === 1 &&
                        <ServiceViewForm selectedServiceType={selectedServiceType} expanded={expanded} handleAccordionChange={handleAccordionChange} />
                    }

                    {
                        openedPage === CurrentActiveServicePage.ViewPage && selectedPatientService && serviceNursesCount > 1 &&
                        <ServiceNurseViewForm selectedServiceType={selectedServiceType} expanded={expanded}
                            handleAccordionChange={handleAccordionChange} />
                    }

                    {
                        openedPage === CurrentActiveServicePage.EditPage && selectedPatientService &&
                        <ServiceEditForm selectedServiceType={selectedServiceType} expanded={expanded} serviceNursesCount={serviceNursesCount}
                            handleAccordionChange={handleAccordionChange} />
                    }

                    {
                        openedPage === CurrentActiveServicePage.ChangeNursePage && selectedPatientService &&
                        <NurseChangeForm selectedServiceType={selectedServiceType} handleCloseNurseChangeClick={handleCloseNurseChangeClick} />
                    }


                </Stack>
            </LocalizationProvider>
        </Box>

    );

}
